import React, { useState, useEffect } from 'react';
import { STANDARD_SCENARIO_OPTIONS, DEFAULT_SCENARIOS, formatScenarioLabel, formatScenarioPrice } from '../../utils/elasticityCalculations';

/**
 * Settings panel for the Elasticity Heatmap view.
 * - Days to Exit slider (adapted from SimulationSettingsPanel)
 * - Scenario column toggles (multi-select buttons replacing the underlying gain slider)
 */
const HeatmapSettingsPanel = ({
  maxDays = 365,
  maxExpirationDate = null,
  daysToExit,
  onDaysChange,
  selectedScenarios,
  onScenariosChange,
  contractCount = 0,
  defaultExpanded = false,
  defaultDays = null,
  onReset = null,
  isOverridden = false, // true when user has moved the slider (fixed days mode)
  daysToExitMode = 'per-contract', // 'per-contract' | 'fixed' | 'percent'
  onModeChange = null,
  daysToExitPercent = null, // 0-1 float
  onPercentChange = null,
  // The toggle list to render. Defaults to the original integer-only set so
  // Basic/Intermediate are identical to before; Intraday passes ALL_SCENARIOS
  // (incl. sub-1% values).
  scenarioOptions = STANDARD_SCENARIO_OPTIONS,
  // The mode's shipped default set, used to detect "changed from default".
  // Defaults to DEFAULT_SCENARIOS (Basic/Intermediate); Intraday passes its own.
  defaultScenarioSet = DEFAULT_SCENARIOS,
  // Underlying price shared by every contract in the result set (0DTE only).
  // When set, each scenario chip also shows the price that move implies.
  scenarioBasePrice = null,
  // IV model toggle (intraday only). showIvModelToggle gates visibility so
  // Basic/Intermediate never render it. ivModel: 'strike' | 'moneyness'.
  showIvModelToggle = false,
  ivModel = 'strike',
  onIvModelChange = null,
  // Earnings vol-crush toggle (intraday only, and only when an earnings event is
  // actually in some contract's window).
  showVolCrushToggle = false,
  volCrush = false,
  onVolCrushChange = null,
  // Time-to-exit unit toggle (intraday only). daysToExit stays in fractional DAYS;
  // this only changes how the slider presents/steps it.
  showUnitToggle = false,
  daysToExitUnit = 'days',
  onUnitChange = null,
  maxFractionalDays = null,
}) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  // Detect if settings have changed from defaults
  const hasChanges = (() => {
    if (isOverridden) return true;
    if (daysToExitMode === 'percent') return true;
    if (defaultDays !== null && daysToExit !== defaultDays) return true;
    if (selectedScenarios.size !== defaultScenarioSet.size) return true;
    for (const s of defaultScenarioSet) {
      if (!selectedScenarios.has(s)) return true;
    }
    return false;
  })();

  // Clamp days if maxDays shrinks
  useEffect(() => {
    if (daysToExit > maxDays) {
      onDaysChange(maxDays);
    }
  }, [maxDays, daysToExit, onDaysChange]);

  const toggleExpanded = () => setIsExpanded(prev => !prev);

  const handleScenarioToggle = (scenarioPct) => {
    const next = new Set(selectedScenarios);
    if (next.has(scenarioPct)) {
      next.delete(scenarioPct);
    } else {
      next.add(scenarioPct);
    }
    onScenariosChange(next);
  };

  // Calculate exit date
  const getTargetDate = (days) => {
    const today = new Date();
    return new Date(today.getTime() + (days * 24 * 60 * 60 * 1000));
  };

  const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const formatDateWithDays = (date, days) => `${formatDate(date)} (${days} d.)`;

  const getFormattedTargetDate = () => {
    const targetDate = getTargetDate(daysToExit);
    return formatDateWithDays(targetDate, daysToExit);
  };

  const getFormattedMaxDate = () => {
    if (maxExpirationDate) {
      const date = new Date(maxExpirationDate);
      return formatDateWithDays(date, maxDays);
    }
    const maxDate = getTargetDate(maxDays);
    return formatDateWithDays(maxDate, maxDays);
  };

  const selectedCount = selectedScenarios.size;

  // Fixed days-to-exit slider config. Defaults to the original whole-day slider;
  // intraday (showUnitToggle) switches units while keeping the value in fractional
  // days. Non-intraday keeps the exact original min/max/step/fill/labels.
  const fmtHM = (hours) => {
    const totalMin = Math.max(0, Math.round(hours * 60));
    const h = Math.floor(totalMin / 60);
    const m = totalMin % 60;
    if (h === 0) return `${m}m`;
    if (m === 0) return `${h}h`;
    return `${h}h ${m}m`;
  };
  // Absolute simulated-sale datetime = now + hold (fractional days), shown in ET
  // since the whole intraday feature is anchored to the 16:00-ET close.
  const fmtExitDateTime = (fracDays) => {
    const d = new Date(Date.now() + fracDays * 86400000);
    return d.toLocaleString('en-US', {
      timeZone: 'America/New_York', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
    }) + ' ET';
  };
  const fracMax = (maxFractionalDays != null ? maxFractionalDays : maxDays) || 1;
  let slMin = 1, slMax = maxDays, slStep = 1, slVal = daysToExit, slToDays = (v) => v;
  let slLeft = '1 d.', slMid = `${Math.floor(maxDays / 2)} d.`, slRight = getFormattedMaxDate();
  let fixedHeaderLabel = getFormattedTargetDate();
  if (showUnitToggle && daysToExitUnit === 'hours') {
    slMin = 0; slMax = Math.max(1, Math.floor(fracMax * 24)); slStep = 1;
    slVal = Math.min(daysToExit * 24, slMax); slToDays = (v) => v / 24;
    slLeft = '0h'; slMid = `${Math.round(slMax / 2)}h`; slRight = `${slMax}h`;
    fixedHeaderLabel = `${fmtHM(daysToExit * 24)} → ${fmtExitDateTime(daysToExit)}`;
  } else if (showUnitToggle && daysToExitUnit === 'min30') {
    slMin = 0; slMax = Math.max(0.5, fracMax * 24); slStep = 0.5;
    slVal = Math.min(daysToExit * 24, slMax); slToDays = (v) => v / 24;
    slLeft = '0'; slMid = fmtHM(slMax / 2); slRight = fmtHM(slMax);
    fixedHeaderLabel = `${fmtHM(daysToExit * 24)} → ${fmtExitDateTime(daysToExit)}`;
  } else if (showUnitToggle) {
    slMin = 0; slMax = Math.max(1, Math.ceil(fracMax));
    slVal = Math.min(daysToExit, slMax);
    slLeft = '0 d.'; slMid = `${Math.round(slMax / 2)} d.`; slRight = `${slMax} d.`;
    fixedHeaderLabel = daysToExit < 1 ? `${fmtHM(daysToExit * 24)} → ${fmtExitDateTime(daysToExit)}` : getFormattedTargetDate();
  }
  const slFillPct = showUnitToggle
    ? (slMax > slMin ? ((slVal - slMin) / (slMax - slMin)) * 100 : 0)
    : (daysToExit / maxDays) * 100;

  return (
    <div className="simulation-settings bg-gray-800 rounded-lg mb-4">
      {/* Header */}
      <div
        className="flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-gray-750 transition-colors"
        onClick={toggleExpanded}
      >
        <div className="flex items-center gap-3">
          <h3 className="text-sm font-semibold text-gray-200">
            Heatmap Settings
          </h3>
          <span className="text-xs text-gray-400">
            <span className="text-blue-500">
              {daysToExitMode === 'percent'
                ? `${Math.round((daysToExitPercent || 0.30) * 100)}% of exp`
                : isOverridden ? `${daysToExit} days` : 'Per-contract days'
              }, {selectedCount} scenarios
            </span>
            {' '}({contractCount} contracts)
          </span>
        </div>
        <span className={`text-gray-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`}>
          ›
        </span>
      </div>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="px-4 pb-4 space-y-4">
          {/* Days to Exit Section */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <label className="text-xs font-medium text-gray-300">
                  Days to Exit
                </label>
                {/* Mode Toggle */}
                {onPercentChange && onModeChange && (
                  <div className="flex gap-1">
                    <button
                      onClick={() => {
                        onModeChange('fixed');
                        onDaysChange(daysToExit || defaultDays || 45);
                      }}
                      className={`px-2 py-0.5 text-[10px] rounded transition-colors ${
                        daysToExitMode === 'fixed' || daysToExitMode === 'per-contract'
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-700 text-gray-400 hover:bg-gray-600'
                      }`}
                    >
                      Fixed Days
                    </button>
                    <button
                      onClick={() => onPercentChange(daysToExitPercent || 0.30)}
                      className={`px-2 py-0.5 text-[10px] rounded transition-colors ${
                        daysToExitMode === 'percent'
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-700 text-gray-400 hover:bg-gray-600'
                      }`}
                    >
                      % of Exp
                    </button>
                  </div>
                )}
                {/* Unit toggle (intraday only, fixed mode) */}
                {showUnitToggle && onUnitChange && daysToExitMode !== 'percent' && (
                  <div className="flex gap-1">
                    {[['days', 'Days'], ['hours', 'Hours'], ['min30', '30-min']].map(([u, lbl]) => (
                      <button
                        key={u}
                        onClick={() => onUnitChange(u)}
                        className={`px-2 py-0.5 text-[10px] rounded transition-colors ${
                          daysToExitUnit === u
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-700 text-gray-400 hover:bg-gray-600'
                        }`}
                      >
                        {lbl}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <span className="text-sm font-bold text-blue-500">
                {daysToExitMode === 'percent'
                  ? `${Math.round((daysToExitPercent || 0.30) * 100)}% of expiration`
                  : fixedHeaderLabel
                }
              </span>
            </div>

            {daysToExitMode === 'percent' ? (
              /* Percentage slider: 5% to 100% */
              <div className="relative">
                <input
                  type="range"
                  min="5"
                  max="100"
                  value={Math.round((daysToExitPercent || 0.30) * 100)}
                  onChange={(e) => onPercentChange(Number(e.target.value) / 100)}
                  className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer slider"
                  style={{
                    background: `linear-gradient(to right, #218aff 0%, #218aff ${
                      ((Math.round((daysToExitPercent || 0.30) * 100) - 5) / 95) * 100
                    }%, #374151 ${((Math.round((daysToExitPercent || 0.30) * 100) - 5) / 95) * 100}%, #374151 100%)`
                  }}
                />
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>5%</span>
                  <span>50%</span>
                  <span>100%</span>
                </div>
              </div>
            ) : (
              /* Fixed days slider — unit-aware in intraday, original otherwise */
              <div className="relative">
                <input
                  type="range"
                  min={slMin}
                  max={slMax}
                  step={slStep}
                  value={slVal}
                  onChange={(e) => onDaysChange(slToDays(Number(e.target.value)))}
                  className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer slider"
                  style={{
                    background: `linear-gradient(to right, #218aff 0%, #218aff ${slFillPct}%, #374151 ${slFillPct}%, #374151 100%)`
                  }}
                />
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>{slLeft}</span>
                  <span>{slMid}</span>
                  <span>{slRight}</span>
                </div>
              </div>
            )}
          </div>

          {/* Scenario Column Toggles */}
          <div className="space-y-2">
            <label className="text-xs font-medium text-gray-300">
              Underlying % Change Scenarios
            </label>
            <div className="flex flex-wrap gap-1">
              {scenarioOptions.map((scenarioPct) => {
                const isSelected = selectedScenarios.has(scenarioPct);
                const scenarioPrice = formatScenarioPrice(scenarioPct, scenarioBasePrice);
                return (
                  <button
                    key={scenarioPct}
                    onClick={() => handleScenarioToggle(scenarioPct)}
                    className={`px-2 py-1 text-xs rounded transition-colors ${
                      scenarioPrice ? 'flex flex-col items-center leading-tight' : ''
                    } ${
                      isSelected
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                    }`}
                  >
                    <span>{formatScenarioLabel(scenarioPct)}</span>
                    {scenarioPrice && (
                      <span className={`text-[10px] ${isSelected ? 'text-blue-100' : 'text-gray-400'}`}>
                        {scenarioPrice}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* IV Model toggle (intraday only) */}
          {showIvModelToggle && onIvModelChange && (
            <div className="space-y-2">
              <label className="text-xs font-medium text-gray-300">
                IV Model
              </label>
              <div className="flex gap-1">
                <button
                  onClick={() => onIvModelChange('moneyness')}
                  title="Slide IV along the volatility smile as the underlying moves (recommended for short-dated). IV is re-read at the contract's new moneyness."
                  className={`px-2 py-1 text-xs rounded transition-colors ${
                    ivModel === 'moneyness'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                  }`}
                >
                  Sticky-moneyness
                </button>
                <button
                  onClick={() => onIvModelChange('strike')}
                  title="Hold IV fixed at the strike as the underlying moves (simplest; least realistic on the wings)."
                  className={`px-2 py-1 text-xs rounded transition-colors ${
                    ivModel === 'strike'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                  }`}
                >
                  Sticky-strike
                </button>
              </div>
            </div>
          )}

          {/* Earnings vol-crush toggle (intraday, only when earnings in window) */}
          {showVolCrushToggle && onVolCrushChange && (
            <div className="space-y-2">
              <label className="text-xs font-medium text-gray-300">
                Earnings Vol-Crush
              </label>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => onVolCrushChange(!volCrush)}
                  title="When a contract's holding window crosses a scheduled earnings date, collapse IV to the post-event diffusive level. The contract can then show a loss through earnings even on a favorable move (vol crush)."
                  className={`px-2 py-1 text-xs rounded transition-colors ${
                    volCrush
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                  }`}
                >
                  {volCrush ? '⚡ Modeling crush' : 'Crush off'}
                </button>
                <span className="text-[10px] text-gray-500">
                  Earnings detected in window
                </span>
              </div>
            </div>
          )}

          {/* Reset Button */}
          {hasChanges && onReset && (
            <div className="pt-2">
              <button
                onClick={onReset}
                className="w-1/3 px-4 py-2 text-sm font-medium rounded transition-colors bg-gray-700 text-gray-300 hover:bg-gray-600"
              >
                Reset
              </button>
            </div>
          )}
        </div>
      )}

      {/* Custom Slider Styles */}
      <style jsx>{`
        input[type="range"]::-webkit-slider-thumb {
          appearance: none;
          width: 16px;
          height: 16px;
          background: #218aff;
          cursor: pointer;
          border-radius: 50%;
          border: 2px solid #1f2937;
        }

        input[type="range"]::-moz-range-thumb {
          width: 16px;
          height: 16px;
          background: #218aff;
          cursor: pointer;
          border-radius: 50%;
          border: 2px solid #1f2937;
        }

        input[type="range"]:focus {
          outline: none;
        }

        input[type="range"]:focus::-webkit-slider-thumb {
          box-shadow: 0 0 0 3px rgba(33, 138, 255, 0.2);
        }
      `}</style>
    </div>
  );
};

export default HeatmapSettingsPanel;
